import { useState, useRef, useCallback, useEffect } from 'react';
import { useRecognizeFaces } from '../../api/faceai';
import type { RecognizeResult } from '../../api/faceai';

// ── Types ──

type StreamSource = 'webcam' | 'url';
type DetectionFrequency = 1000 | 2000 | 5000;

interface DetectionLogEntry {
  id: string;
  timestamp: Date;
  faces: RecognizeResult[];
  processingMs: number;
}

interface StreamStats {
  fps: number;
  facesDetected: number;
  recognizedCount: number;
}

// ── Constants ──

const FACE_BOX_COLORS = {
  known: '#22c55e',
  unknown: '#ef4444',
} as const;

// ── Helpers ──

function formatTime(date: Date): string {
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function canvasToFile(canvas: HTMLCanvasElement): Promise<File> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(new File([blob!], 'frame.jpg', { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.85
    );
  });
}

// ── Component ──

export default function VideoStreamPanel() {
  // Stream state
  const [source, setSource] = useState<StreamSource>('webcam');
  const [streamUrl, setStreamUrl] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [frequency, setFrequency] = useState<DetectionFrequency>(2000);

  // Detection state
  const [currentFaces, setCurrentFaces] = useState<RecognizeResult[]>([]);
  const [log, setLog] = useState<DetectionLogEntry[]>([]);
  const [stats, setStats] = useState<StreamStats>({
    fps: 0,
    facesDetected: 0,
    recognizedCount: 0,
  });

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fpsFrameCount = useRef(0);
  const fpsLastTime = useRef(performance.now());
  const animFrameRef = useRef<number>(0);
  const logIdCounter = useRef(0);

  const recognizeMutation = useRecognizeFaces();

  // ── Start / Stop Stream ──

  const startWebcam = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      console.error('[VideoStreamPanel] getUserMedia non disponible (HTTPS requis)');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsStreaming(true);
    } catch (err: any) {
      console.error('[VideoStreamPanel] Webcam access failed:', err?.name, err?.message);
      // Fallback sans contraintes si OverconstrainedError
      if (err?.name === 'OverconstrainedError') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
          }
          setIsStreaming(true);
        } catch (e2) {
          console.error('[VideoStreamPanel] Fallback also failed:', e2);
        }
      }
    }
  }, []);

  const startUrlStream = useCallback(() => {
    if (!streamUrl || !videoRef.current) return;
    videoRef.current.src = streamUrl;
    videoRef.current.crossOrigin = 'anonymous';
    videoRef.current.play().catch(console.error);
    setIsStreaming(true);
  }, [streamUrl]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    setIsStreaming(false);
    setCurrentFaces([]);
  }, []);

  const toggleStream = useCallback(() => {
    if (isStreaming) {
      stopStream();
    } else {
      source === 'webcam' ? startWebcam() : startUrlStream();
    }
  }, [isStreaming, source, stopStream, startWebcam, startUrlStream]);

  // ── Frame Capture & Recognition ──

  const captureAndRecognize = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;
    if (recognizeMutation.isPending) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    try {
      const file = await canvasToFile(canvas);
      const result = await recognizeMutation.mutateAsync(file);
      setCurrentFaces(result.results);

      const entry: DetectionLogEntry = {
        id: `log-${logIdCounter.current++}`,
        timestamp: new Date(),
        faces: result.results,
        processingMs: result.processing_time_ms,
      };
      setLog((prev) => [entry, ...prev].slice(0, 200));

      setStats((prev) => ({
        ...prev,
        facesDetected: prev.facesDetected + result.faces_detected,
        recognizedCount:
          prev.recognizedCount +
          result.results.filter((r: RecognizeResult) => r.person_name !== 'unknown').length,
      }));
    } catch (err: any) {
      console.error('[VideoStreamPanel] Recognition failed:', err?.response?.status, err?.response?.data, err?.message);
      // Ajouter l'erreur dans le log visible
      const entry: DetectionLogEntry = {
        id: `err-${logIdCounter.current++}`,
        timestamp: new Date(),
        faces: [],
        processingMs: 0,
      };
      setLog((prev) => [entry, ...prev].slice(0, 200));
    }
  }, [recognizeMutation]);

  // ── Recognition interval ──

  useEffect(() => {
    if (!isStreaming) return;
    intervalRef.current = setInterval(captureAndRecognize, frequency);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isStreaming, frequency, captureAndRecognize]);

  // ── Render loop: draw video + overlays ──

  useEffect(() => {
    if (!isStreaming) return;

    const render = () => {
      const video = videoRef.current;
      const overlay = overlayCanvasRef.current;
      if (!video || !overlay || video.readyState < 2) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      overlay.width = video.videoWidth;
      overlay.height = video.videoHeight;
      const ctx = overlay.getContext('2d')!;
      ctx.clearRect(0, 0, overlay.width, overlay.height);

      // Draw video frame
      ctx.drawImage(video, 0, 0);

      // Draw face overlays
      for (const face of currentFaces) {
        const [x, y, w, h] = face.bbox;
        const isKnown = face.person_name !== 'unknown';
        const color = isKnown ? FACE_BOX_COLORS.known : FACE_BOX_COLORS.unknown;

        // Bounding box
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, w, h);

        // Semi-transparent background for label
        const label = isKnown
          ? `${face.person_name} (${(face.recognition_confidence * 100).toFixed(1)}%)`
          : 'Unknown';
        const analysis = face.analysis || {};
        const age = analysis.age as number | undefined;
        const gender = analysis.gender as string | undefined;
        const emotion = analysis.dominant_emotion as string | undefined;
        const infoLine = [age && `${age}y`, gender, emotion].filter(Boolean).join(' / ');

        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        const labelWidth = Math.max(
          ctx.measureText(label).width,
          ctx.measureText(infoLine).width
        );
        const labelHeight = infoLine ? 40 : 22;

        ctx.fillStyle = `${color}cc`;
        ctx.fillRect(x, y - labelHeight - 4, labelWidth + 12, labelHeight + 4);

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillText(label, x + 6, y - (infoLine ? 24 : 8));

        if (infoLine) {
          ctx.font = '12px Inter, system-ui, sans-serif';
          ctx.fillText(infoLine, x + 6, y - 8);
        }
      }

      // FPS counter
      fpsFrameCount.current++;
      const now = performance.now();
      if (now - fpsLastTime.current >= 1000) {
        setStats((prev) => ({ ...prev, fps: fpsFrameCount.current }));
        fpsFrameCount.current = 0;
        fpsLastTime.current = now;
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isStreaming, currentFaces]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  // ── Render ──

  return (
    <div className="flex h-full gap-4 bg-[#0b1120] text-slate-200">
      {/* Main video area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="relative bg-slate-900 rounded-xl overflow-hidden flex-1 min-h-[400px]">
          {/* Hidden video element */}
          <video
            ref={videoRef}
            className="hidden"
            playsInline
            muted
            autoPlay
          />
          {/* Hidden capture canvas */}
          <canvas ref={canvasRef} className="hidden" />
          {/* Visible overlay canvas with video + face boxes */}
          <canvas
            ref={overlayCanvasRef}
            className="w-full h-full object-contain"
            style={{ background: '#0f172a' }}
          />

          {/* No stream placeholder */}
          {!isStreaming && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-slate-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                </div>
                <p className="text-slate-400 text-sm">
                  Select a source and start streaming
                </p>
              </div>
            </div>
          )}

          {/* Live indicator */}
          {isStreaming && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-semibold text-white uppercase tracking-wider">
                Live
              </span>
            </div>
          )}

          {/* Processing indicator */}
          {recognizeMutation.isPending && (
            <div className="absolute top-4 right-4 bg-black/60 px-3 py-1.5 rounded-full">
              <span className="text-xs text-cyan-400 animate-pulse">
                Processing...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto">
        {/* Stream controls */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
            Stream Controls
          </h3>

          {/* Source selector */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSource('webcam')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                source === 'webcam'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              Webcam
            </button>
            <button
              onClick={() => setSource('url')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                source === 'url'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              URL Stream
            </button>
          </div>

          {/* URL input */}
          {source === 'url' && (
            <input
              type="text"
              placeholder="rtsp:// or http:// stream URL"
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              className="w-full px-3 py-2 mb-3 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500"
            />
          )}

          {/* Start / Stop */}
          <button
            onClick={toggleStream}
            className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              isStreaming
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-cyan-600 hover:bg-cyan-500 text-white'
            }`}
          >
            {isStreaming ? 'Stop Stream' : 'Start Stream'}
          </button>
        </div>

        {/* Detection frequency */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
            Detection Frequency
          </h3>
          <div className="flex gap-2">
            {([1000, 2000, 5000] as DetectionFrequency[]).map((freq) => (
              <button
                key={freq}
                onClick={() => setFrequency(freq)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  frequency === freq
                    ? 'bg-cyan-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {freq / 1000}s
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
            Stats
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-lg font-bold text-cyan-400">{stats.fps}</div>
              <div className="text-[10px] text-slate-500 uppercase">FPS</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-400">
                {stats.facesDetected}
              </div>
              <div className="text-[10px] text-slate-500 uppercase">Detected</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-400">
                {stats.recognizedCount}
              </div>
              <div className="text-[10px] text-slate-500 uppercase">Recognized</div>
            </div>
          </div>
        </div>

        {/* Live recognition log */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 flex-1 min-h-0 flex flex-col">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
            Recognition Log
          </h3>
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0 pr-1 scrollbar-thin scrollbar-thumb-slate-700">
            {log.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">
                No detections yet
              </p>
            )}
            {log.map((entry) => (
              <div
                key={entry.id}
                className="bg-slate-800/60 rounded-lg px-3 py-2 text-xs"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-slate-500 font-mono">
                    {formatTime(entry.timestamp)}
                  </span>
                  <span className="text-slate-600">
                    {entry.processingMs.toFixed(0)}ms
                  </span>
                </div>
                {entry.faces.length === 0 ? (
                  <span className="text-slate-500 italic">No faces</span>
                ) : (
                  entry.faces.map((face, i) => {
                    const isKnown = face.person_name !== 'unknown';
                    return (
                      <div key={i} className="flex items-center gap-2 mt-0.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            isKnown ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        />
                        <span className={isKnown ? 'text-green-400' : 'text-red-400'}>
                          {face.person_name}
                        </span>
                        <span className="text-slate-500 ml-auto">
                          {(face.recognition_confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
